import isHotkey from 'is-hotkey';
import { useCallback } from 'react';
import { batch } from 'react-redux';
import { fastEqual } from '@flowus/common/utils/tools';
import { getEditorModelByEditorKey } from 'src/editor/editor/uikit/editable-models';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import type { SelectBlock } from 'src/redux/types';
import {
  filterDescendantId,
  getAllBlockInfoFromDomContent,
  isAlreadyExistAncestors,
} from 'src/utils/block-utils';
import { querySelectorFromMainContent } from 'src/utils/dom';
import { parseEditorKeyToBlockId } from 'src/utils/editor-key-to-id';
import { getDynamicPageId } from 'src/utils/getPageId';
import { idsToSelectedBlocks } from 'src/utils/select-block-util';

export const useArrowKey = () => {
  const arrowKey = (event: KeyboardEvent) => {
    if (cache.ui.selectedCells.length > 0) {
      return;
    }
    const isInRight = getDynamicPageId() === getDynamicPageId('right');
    if (isHotkey(['ArrowUp', 'shift+ArrowUp', 'ArrowDown', 'shift+ArrowDown'])(event)) {
      const editorKey = document.activeElement
        ?.closest('[data-editor]')
        ?.getAttribute('data-editor');
      if (editorKey) {
        // feat https://flowus.cn/efa4debe-000c-413b-8d30-8427292224a5#4718b931-2f16-4495-a178-6e7ca89f1d8b
        let model = getEditorModelByEditorKey(editorKey);
        if (!model) {
          model = getEditorModelByEditorKey(`${editorKey}_mock`);
        }
        if (!model.selection) return;
        if (!event.shiftKey) return;
        const { focusOffset } = model.selection;
        if (focusOffset !== 0 && focusOffset !== model.content.length) return;
        if (isHotkey('shift+ArrowUp')(event) && focusOffset === model.content.length) return;
        if (isHotkey('shift+ArrowDown')(event) && focusOffset === 0) return;
        event.preventDefault();
        const blockId = parseEditorKeyToBlockId(editorKey);
        dispatch(uiActions.updateSelectBlocks(idsToSelectedBlocks([blockId])));
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.getSelection().removeAllRanges();
        document.dispatchEvent(new Event('_clearTextSelection'));
        return;
      }

      event.preventDefault();

      const pageId = getDynamicPageId();

      if (cache.ui.selectedBlocks.length === 0) {
        const page = cache.blocks[pageId];
        if (page) {
          const selectedBlocks = page.subNodes.slice(0, 1).map((id) => {
            return { blockId: id };
          });
          dispatch(uiActions.updateSelectBlocks(selectedBlocks));
        }
      } else {
        const isArrowDown = isHotkey(['ArrowDown', 'shift+ArrowDown'])(event);
        const focusIndex = cache.ui.isSelectTopToBottom ? 0 : cache.ui.selectedBlocks.length - 1;
        const focusedBlockId = cache.ui.selectedBlocks[focusIndex]?.blockId;
        const firstFocusBlockViewId = cache.ui.selectedBlocks[focusIndex]?.viewId;
        const lastBlockIndex = cache.ui.isSelectTopToBottom
          ? cache.ui.selectedBlocks.length - 1
          : 0;
        const lastSelectedBlockId = cache.ui.selectedBlocks[lastBlockIndex]?.blockId;
        const lastFocusBlockViewId = cache.ui.selectedBlocks[lastBlockIndex]?.viewId;
        if (!focusedBlockId || !lastSelectedBlockId) return;

        const blockInfo = getAllBlockInfoFromDomContent(isInRight);
        if (!blockInfo) return;

        const focusedBlockIndex = blockInfo.findIndex(
          (v) => v.blockId === focusedBlockId && v.viewId === firstFocusBlockViewId
        );
        let lastSelectedBlockIndex = blockInfo.findIndex(
          (v) => v.blockId === lastSelectedBlockId && v.viewId === lastFocusBlockViewId
        );
        let isTopToBottom: boolean;
        if (focusedBlockIndex === lastSelectedBlockIndex) {
          // 如果已经存在一个选中的block，方向键向下的话就是从上往下多选
          isTopToBottom = isArrowDown;
        } else {
          isTopToBottom = focusedBlockIndex < lastSelectedBlockIndex;
        }
        let loop = false;
        let newSelectedBlock: SelectBlock[] | undefined;
        /**
         * 这里的实现机制是只要选中就
         */
        do {
          const nextLastSelectedBlockIndex = lastSelectedBlockIndex + (isArrowDown ? 1 : -1);
          lastSelectedBlockIndex = nextLastSelectedBlockIndex;
          const nextSelectedBlock = blockInfo[nextLastSelectedBlockIndex];
          if (!nextSelectedBlock) {
            // 如果不存在说明已经是最顶部或者底部了
            return;
          }
          if (event.shiftKey) {
            if (isArrowDown) {
              // 特殊case:从下往上选，中间有组的，再往下就需要跳过组里的元素
              const exist = isAlreadyExistAncestors(cache.ui.selectedBlocks, nextSelectedBlock);
              if (exist) {
                loop = true;
                continue;
              }
            }

            const startIndex = isTopToBottom ? focusedBlockIndex : nextLastSelectedBlockIndex;
            const endIndex = isTopToBottom ? nextLastSelectedBlockIndex : focusedBlockIndex;
            newSelectedBlock = blockInfo.slice(startIndex, endIndex + 1);
            newSelectedBlock = filterDescendantId(newSelectedBlock);
            // 如果没变说明选中了组里的，需要继续往下找下一个
            loop = fastEqual(cache.ui.selectedBlocks, newSelectedBlock);
          } else {
            // 单选
            newSelectedBlock = [nextSelectedBlock];
            let node: Element | null;
            if (nextSelectedBlock.viewId) {
              node = querySelectorFromMainContent(
                `[data-block-id='${nextSelectedBlock.blockId}'][data-view-id='${nextSelectedBlock.viewId}']`,
                isInRight
              );
            } else {
              node = querySelectorFromMainContent(
                `[data-block-id='${nextSelectedBlock.blockId}']`,
                isInRight
              );
            }
            if (node) {
              node.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }
        } while (loop);

        batch(() => {
          if (newSelectedBlock) {
            dispatch(uiActions.updateSelectBlocks(newSelectedBlock));
            dispatch(
              uiActions.update({
                isSelectTopToBottom: isTopToBottom,
              })
            );
          }
        });
      }
    }
  };
  return useCallback(arrowKey, []);
};
